import { useEffect, useState } from 'react';
import { studentMatches } from '../lib/search';
import { MobileHeader, HeaderAction, ActionSheet } from '../components/MobileHeader';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ChevronRight, ChevronDown, Instagram, CreditCard, X, ArrowDownUp, Clock, Filter, UserPlus2, SlidersHorizontal, MoreHorizontal, Wallet } from 'lucide-react';
import { useLang } from '../i18n';
import { api } from '../api';
import { Avatar } from './Dashboard';
import StudentModal from '../components/StudentModal';
import PaymentModal from '../components/PaymentModal';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

const getLastMethod = () => { try { return localStorage.getItem('bachata_last_method') || 'cash'; } catch { return 'cash'; } };

const PAGE_SIZE = 20;

// Generate and open a vCard for iOS native contact creation
const openVCard = (s) => {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${s.first_name} ${s.last_name}`,
    `N:${s.last_name};${s.first_name};;;`,
    'ORG:Bachata / Zouk Brésilien',
    s.email ? `EMAIL:${s.email}` : '',
    s.phone ? `TEL;TYPE=CELL:${s.phone}` : '',
    s.instagram ? `X-SOCIALPROFILE;type=instagram:${s.instagram.replace('@', '')}` : '',
    ['role', 'dance_background', 'experience_years', 'notes'].some(k => s[k])
      ? `NOTE:Rôle: ${s.role || '—'} | Danse: ${s.dance_background || '—'} | Exp: ${s.experience_years || 0} ans | ${s.notes || ''}`.trim()
      : '',
    'END:VCARD',
  ].filter(Boolean).join('\r\n');
  const blob = new Blob([lines], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${s.first_name}_${s.last_name}.vcf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function Spinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-[#0288D1]/30 border-t-[#0288D1] rounded-full animate-spin" />
    </div>
  );
}

export default function Students() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [groupCourses, setGroupCourses] = useState([]);
  const [finance, setFinance] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');
  const [filterRole, setFilterRole] = useState(''); // '', 'leader', 'follower'
  const [sortBy, setSortBy] = useState('alpha'); // 'alpha' | 'recent'
  const [showModal, setShowModal] = useState(false);
  const [payStudent, setPayStudent] = useState(null);
  const [soldeStudent, setSoldeStudent] = useState(null); // { student, fin }
  const [showSoldeAll, setShowSoldeAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [followersOpen, setFollowersOpen] = useState(true);
  const [leadersOpen, setLeadersOpen] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [studentMenu, setStudentMenu] = useState(null);  // mobile : { s, fin, remaining } — menu ⋯ d'une tuile élève

  const [quickPayingId, setQuickPayingId] = useState(null);

  const handleQuickPay = async (e, s, fin) => {
    e.stopPropagation();
    if (quickPayingId) return;
    const remaining = fin?.remaining || 0;
    const amount = remaining > 0 ? remaining : 20;
    const method = getLastMethod();
    const pkg = (fin?.packages || []).find(p => Math.max(0, p.total_price - (p.paid_amount || 0)) > 0);
    setQuickPayingId(s.id);
    try {
      await api.post('/payments', {
        student_id: s.id,
        amount: parseFloat(amount),
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        method,
        notes: '',
        package_id: pkg?.id || null,
        group_course_id: null,
      });
      toast.success(`✓ ${amount} CHF · ${s.first_name} ${s.last_name}`);
      fetchData();
    } catch {
      toast.error('Erreur paiement');
    } finally { setQuickPayingId(null); }
  };

  const fetchData = async () => {
    const [s, f, gc] = await Promise.all([
      api.get('/students'),
      api.get('/finance').catch(() => []),
      api.get('/group-courses').catch(() => []),
    ]);
    setStudents(s);
    setFinance(f);
    setGroupCourses(gc);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // Reset page on filter/search change
  useEffect(() => { setPage(1); }, [search, filterCourse, filterRole, sortBy]);

  const filtered = students.filter(s => {
    const matchSearch = studentMatches(s, search);
    if (!matchSearch) return false;
    if (filterRole && s.role !== filterRole) return false;
    if (filterCourse === 'all') return true;
    if (filterCourse === 'private') {
      const fin = finance.find(f => f.student?.id === s.id);
      return fin?.packages?.some(p => p.type === 'private');
    }
    const gc = groupCourses.find(c => c.id === filterCourse);
    return gc?.enrolled_students?.some(e => e.student_id === s.id) ?? false;
  }).sort((a, b) => {
    if (sortBy === 'recent') {
      const da = a.created_at || a.enrolled_at || '';
      const db_ = b.created_at || b.enrolled_at || '';
      return db_.localeCompare(da);
    }
    return `${a.last_name} ${a.first_name}`.toLowerCase().localeCompare(`${b.last_name} ${b.first_name}`.toLowerCase(), 'fr');
  });

  const getFinance = (id) => finance.find(f => f.student?.id === id);
  const handleSaved = () => { setShowModal(false); fetchData(); };

  if (loading) return <Spinner />;

  const followers = filtered.filter(s => s.role === 'follower');
  const leaders = filtered.filter(s => s.role === 'leader');
  const noRole = filtered.filter(s => !s.role);

  // Pagination applies only to noRole (when no role split) or for very large groups
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const hasPagination = totalPages > 1;

  const StudentCard = ({ s }) => {
    const fin = getFinance(s.id);
    const remaining = fin?.remaining || 0;
    const pkg = fin?.packages?.[0];
    const payButtons = remaining > 0 && (
      <div className="flex items-center gap-1.5 sm:gap-1">
        <button
          onClick={e => { e.stopPropagation(); setSoldeStudent({ student: s, fin }); }}
          className="text-xs font-bold text-white bg-slate-600 hover:bg-slate-700 px-2 py-1.5 rounded-lg transition-all active:scale-95"
          data-testid="solde-btn"
          title="Solder tout"
        >
          Solde
        </button>
        <button
          onClick={e => handleQuickPay(e, s, fin)}
          disabled={quickPayingId === s.id}
          className="text-xs font-bold text-white bg-[#26A69A] hover:bg-[#219e92] px-2 py-1.5 rounded-lg transition-all active:scale-95 disabled:opacity-60 whitespace-nowrap"
          data-testid="quick-pay-btn"
        >
          {quickPayingId === s.id ? '…' : `✓ ${remaining} CHF`}
        </button>
        <button
          onClick={e => { e.stopPropagation(); setPayStudent({ student: s, ...fin }); }}
          className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
          data-testid="open-payment-modal-btn"
          title="Options de paiement"
        >
          <CreditCard size={14} className="text-slate-500" />
        </button>
      </div>
    );

    return (
      <div
        onClick={() => navigate(`/students/${s.id}`)}
        className="bg-white rounded-2xl p-2 lg:px-3 lg:py-2 border border-slate-100 shadow-sm hover:shadow-md cursor-pointer active:scale-[0.98] transition-all"
        data-testid="student-card"
      >
        {/* Mobile : tuile compacte, toujours de la même hauteur (2 colonnes F / L) — v2.14 : plus petite */}
        <div className="mobile-only h-[66px] flex flex-col justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
            <div className="min-w-0 leading-tight">
              <div className="font-bold text-slate-900 text-[13px] truncate">{s.first_name}</div>
              <div className="text-[13px] text-slate-600 truncate">{s.last_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {remaining > 0 ? (
              <button onClick={e => handleQuickPay(e, s, fin)} disabled={quickPayingId === s.id}
                className="flex-1 min-w-0 h-7 rounded-lg bg-[#26A69A] text-white text-xs font-bold truncate px-1.5 active:scale-95 disabled:opacity-60"
                data-testid="quick-pay-btn-mobile">
                {quickPayingId === s.id ? '…' : `✓ ${remaining} CHF`}
              </button>
            ) : (
              <span className="flex-1 min-w-0 text-[11px] text-slate-400 truncate">
                {pkg ? (pkg.type === 'group' ? `✓ ${fin.classes_remaining} cours restants` : `✓ ${fin.hours_remaining}h restantes`) : ''}
              </span>
            )}
            <button onClick={e => { e.stopPropagation(); setStudentMenu({ s, fin, remaining }); }}
              aria-label="Actions" data-testid={`student-menu-${s.id}`}
              className="w-7 h-7 shrink-0 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 active:bg-slate-200">
              <MoreHorizontal size={16} />
            </button>
          </div>
        </div>

        {/* PC : tuile compacte, toujours de la même hauteur (v2.14) */}
        <div className="mobile-hidden h-[52px] flex items-center gap-2.5">
          <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
          <div className="flex-1 min-w-0 leading-tight">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="font-bold text-slate-900 text-sm truncate">{s.first_name} {s.last_name}</div>
              {s.role && (
                <span className={`shrink-0 text-[10px] font-extrabold px-1.5 py-0.5 rounded-md ${
                  s.role === 'leader' ? 'bg-[#E1F5FE] text-[#0288D1]' : 'bg-pink-50 text-pink-600'
                }`} data-testid={`student-role-badge-${s.id}`}>
                  {s.role === 'leader' ? 'L' : 'F'}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400 truncate h-4 mt-0.5 flex items-center gap-1">
              {pkg ? (pkg.type === 'group' ? `${fin.classes_remaining} cours restants` : `${fin.hours_remaining}h restantes`)
                : s.instagram ? <><Instagram size={10} className="shrink-0" /><span className="truncate">{s.instagram}</span></> : ''}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {remaining > 0 ? payButtons : pkg ? (
              <span className="text-[10px] font-semibold text-[#26A69A] bg-[#26A69A]/10 px-1.5 py-0.5 rounded-full">✓ À jour</span>
            ) : null}
            <button
              onClick={e => { e.stopPropagation(); openVCard(s); }}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
              title="Ajouter aux contacts"
              data-testid={`vcard-btn-${s.id}`}
            >
              <UserPlus2 size={12} className="text-slate-400" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const RoleGroup = ({ label, color, students: list, isOpen, onToggle }) => {
    const total = Math.ceil(list.length / PAGE_SIZE);
    const start = (page - 1) * PAGE_SIZE;
    const paged = list.slice(start, start + PAGE_SIZE);
    return (
      <div>
        <button
          onClick={onToggle}
          className="w-full flex items-center gap-1.5 mb-2 group"
          data-testid={`toggle-${label.toLowerCase()}`}
        >
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <span className="text-xs font-bold uppercase tracking-wide flex-1 text-left" style={{ color }}>{label}</span>
          <span className="text-xs text-slate-400 font-medium">{list.length}</span>
          <ChevronDown size={12} className={cn('text-slate-400 transition-transform duration-200 ml-1', isOpen ? 'rotate-180' : '')} />
        </button>
        {isOpen && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
            {list.length === 0
              ? <div className="text-xs text-slate-300 text-center py-4">—</div>
              : paged.map(s => <StudentCard key={s.id} s={s} />)
            }
            {total > 1 && (
              <Pagination page={page} total={total} onChange={setPage} />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="students-page">
      <MobileHeader title={t('students.title')} count={students.length}
        search={{ value: search, onChange: setSearch, placeholder: t('students.search'), scope: 'students' }} actions={<>
        <HeaderAction icon={sortBy === 'recent' ? Clock : ArrowDownUp} label={sortBy === 'recent' ? 'Tri : récents (toucher pour A–Z)' : 'Tri : A–Z (toucher pour récents)'}
          active={sortBy === 'recent'} onClick={() => setSortBy(v => (v === 'alpha' ? 'recent' : 'alpha'))} testId="sort-toggle-btn-mobile" />
        <HeaderAction icon={SlidersHorizontal} label="Filtres" active={showFilters} badge={(filterCourse !== 'all') || null}
          onClick={() => setShowFilters(v => !v)} />
        <HeaderAction icon={Plus} label={t('students.add')} primary onClick={() => setShowModal(true)} testId="add-student-btn-mobile" />
      </>} />

      {/* Search + L/F role filter (PC ; sur mobile → loupe de la barre du titre et 2 colonnes F / L) */}
      <div className="mobile-hidden flex gap-2 mb-3 items-center">
        <div className="flex gap-1 shrink-0">
          {[['', 'Tous'], ['follower', 'F'], ['leader', 'L']].map(([val, label]) => (
            <button key={val} onClick={() => setFilterRole(val)}
              className={cn('px-2.5 py-2 rounded-lg text-xs font-bold border transition-colors',
                filterRole === val
                  ? val === 'follower' ? 'bg-pink-500 text-white border-pink-500'
                    : val === 'leader' ? 'bg-[#0288D1] text-white border-[#0288D1]'
                    : 'bg-slate-700 text-white border-slate-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
              )}
              data-testid={`role-filter-${val || 'all'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('students.search')}
            className="w-full h-11 pl-9 pr-4 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] transition-all"
            data-testid="student-search"
          />
        </div>
      </div>

      {/* Course filters — repliables */}
      <div className={showFilters ? 'mb-4' : 'lg:mb-4'}>
        <button
          onClick={() => setShowFilters(v => !v)}
          className={cn('mobile-hidden flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors mb-2',
            showFilters ? 'bg-[#0288D1]/10 border-[#0288D1]/30 text-[#0288D1]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
          )}
          data-testid="toggle-filters-btn"
        >
          <Filter size={12} />
          Filtres
          {filterCourse !== 'all' && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#0288D1]" />}
          <ChevronDown size={12} className={cn('transition-transform', showFilters && 'rotate-180')} />
        </button>
        {showFilters && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFilterCourse('all')}
              className={cn('px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
                filterCourse === 'all' ? 'bg-[#0288D1] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')}
              data-testid="filter-all-btn">Tous</button>
            {groupCourses.map(gc => (
              <button key={gc.id} onClick={() => setFilterCourse(gc.id === filterCourse ? 'all' : gc.id)}
                className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
                  filterCourse === gc.id ? 'text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')}
                style={filterCourse === gc.id ? { backgroundColor: gc.color || '#0288D1' } : {}}
                data-testid={`filter-course-${gc.id}`}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: filterCourse === gc.id ? 'white' : (gc.color || '#0288D1') }} />
                {gc.name}
              </button>
            ))}
            <button onClick={() => setFilterCourse(filterCourse === 'private' ? 'all' : 'private')}
              className={cn('px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
                filterCourse === 'private' ? 'bg-[#26A69A] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')}
              data-testid="filter-private-btn">Privé</button>
          </div>
        )}
      </div>

      {/* Student list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">{t('students.noStudents')}</div>
      ) : (
        <div>
          {(followers.length > 0 || leaders.length > 0) && (
            <div className="grid grid-cols-2 gap-2 lg:gap-4 mb-4">
              <RoleGroup label="Followers" color="#ec4899" students={followers} isOpen={followersOpen} onToggle={() => setFollowersOpen(v => !v)} />
              <RoleGroup label="Leaders" color="#3b82f6" students={leaders} isOpen={leadersOpen} onToggle={() => setLeadersOpen(v => !v)} />
            </div>
          )}
          {noRole.length > 0 && (
            <div>
              {(followers.length > 0 || leaders.length > 0) && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-xs font-semibold text-slate-400">Rôle non défini ({noRole.length})</span>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-1 lg:gap-0 lg:space-y-2">
                {noRole.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(s => <StudentCard key={s.id} s={s} />)}
              </div>
              {hasPagination && followers.length === 0 && leaders.length === 0 && (
                <Pagination page={page} total={totalPages} onChange={setPage} />
              )}
            </div>
          )}
        </div>
      )}

      {showModal && <StudentModal onClose={() => setShowModal(false)} onSaved={handleSaved} />}
      <ActionSheet open={!!studentMenu} onClose={() => setStudentMenu(null)} testId="student-menu-sheet"
        title={studentMenu && `${studentMenu.s.first_name} ${studentMenu.s.last_name}`} actions={studentMenu ? [
          ...(studentMenu.remaining > 0 ? [
            { icon: Wallet, label: `Solder tout (${studentMenu.remaining} CHF)`, onClick: () => setSoldeStudent({ student: studentMenu.s, fin: studentMenu.fin }) },
            { icon: CreditCard, label: 'Options de paiement', onClick: () => setPayStudent({ student: studentMenu.s, ...studentMenu.fin }) },
          ] : []),
          { icon: UserPlus2, label: 'Ajouter aux contacts', onClick: () => openVCard(studentMenu.s) },
          { icon: ChevronRight, label: 'Ouvrir la fiche', onClick: () => navigate(`/students/${studentMenu.s.id}`) },
        ] : []} />
      {payStudent && (
        <PaymentModal
          studentId={payStudent.student.id}
          studentName={`${payStudent.student.first_name} ${payStudent.student.last_name}`}
          packages={payStudent.packages || []}
          onClose={() => setPayStudent(null)}
          onSaved={() => { setPayStudent(null); fetchData(); }}
        />
      )}
      {soldeStudent && (
        <SoldeModal
          student={soldeStudent.student}
          fin={soldeStudent.fin}
          onClose={() => setSoldeStudent(null)}
          onPaid={() => { setSoldeStudent(null); fetchData(); }}
        />
      )}
    </div>
  );
}

function Pagination({ page, total, onChange }) {
  return (
    <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-slate-100">
      <button onClick={() => onChange(p => Math.max(1, p - 1))} disabled={page === 1}
        className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50 transition-colors">
        ‹ Préc.
      </button>
      <span className="text-xs text-slate-500 font-medium">{page} / {total}</span>
      <button onClick={() => onChange(p => Math.min(total, p + 1))} disabled={page === total}
        className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50 transition-colors">
        Suiv. ›
      </button>
    </div>
  );
}

/* ── SoldeModal — double confirmation pour solder tout ce qu'un élève doit ── */
function SoldeModal({ student, fin, onClose, onPaid }) {
  const [method, setMethod] = useState(() => { try { return localStorage.getItem('bachata_last_method') || 'cash'; } catch { return 'cash'; } });
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const outstandingPkgs = (fin?.packages || []).filter(p => Math.max(0, p.total_price - (p.paid_amount || 0)) > 0);
  const total = outstandingPkgs.reduce((sum, p) => sum + Math.max(0, p.total_price - (p.paid_amount || 0)), 0);

  const handleConfirm = async () => {
    if (!confirmed) { setConfirmed(true); return; }
    setSaving(true);
    try {
      localStorage.setItem('bachata_last_method', method);
      const today = format(new Date(), "yyyy-MM-dd'T'HH:mm");
      if (outstandingPkgs.length > 0) {
        await Promise.all(outstandingPkgs.map(pkg =>
          api.post('/payments', {
            student_id: student.id,
            amount: parseFloat(Math.max(0, pkg.total_price - (pkg.paid_amount || 0)).toFixed(2)),
            date: today,
            method,
            notes: 'Solde complet',
            package_id: pkg.id,
            group_course_id: null,
          })
        ));
      }
      toast.success(`✓ Solde réglé · ${total} CHF · ${student.first_name} ${student.last_name}`);
      onPaid();
    } catch { toast.error('Erreur paiement'); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-sm rounded-t-3xl md:rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="font-extrabold text-slate-900 font-manrope">Solder la balance</div>
            <div className="text-sm text-slate-500">{student.first_name} {student.last_name}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
            <X size={15} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Total */}
          <div className="bg-slate-50 rounded-2xl p-4 text-center">
            <div className="text-4xl font-extrabold text-slate-900 font-manrope">{total} <span className="text-xl text-slate-400">CHF</span></div>
            <div className="text-xs text-slate-400 mt-1">
              {outstandingPkgs.length} forfait{outstandingPkgs.length !== 1 ? 's' : ''} en attente · date d'aujourd'hui
            </div>
          </div>
          {/* Method */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Méthode</label>
            <div className="flex gap-2">
              {[['cash', 'Cash'], ['twint', 'Twint'], ['virement', 'Virement']].map(([val, label]) => (
                <button key={val} onClick={() => { setMethod(val); setConfirmed(false); }}
                  className={cn('flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors',
                    method === val ? 'bg-[#26A69A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                  data-testid={`solde-method-${val}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {/* Confirm */}
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 transition-colors">
              Annuler
            </button>
            <button onClick={handleConfirm} disabled={saving || total <= 0}
              className={cn('flex-1 h-12 rounded-xl font-bold text-base transition-all',
                confirmed ? 'bg-red-500 text-white' : 'bg-[#26A69A] text-white')}
              data-testid="confirm-solde-btn">
              {saving ? '…' : confirmed ? `CONFIRMER ${total} CHF` : `Solder ${total} CHF`}
            </button>
          </div>
          {confirmed && !saving && (
            <p className="text-center text-xs text-red-400 -mt-2">
              Cliquez à nouveau pour confirmer (va dans la Corbeille)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}



/* ── SoldeAllModal — solder tous les paiements en attente jusqu'à une date ── */
function SoldeAllModal({ finance, students, onClose, onPaid }) {
  const today = format(new Date(), "yyyy-MM-dd");
  const [date, setDate] = useState(today);
  const [method, setMethod] = useState(() => { try { return localStorage.getItem('bachata_last_method') || 'cash'; } catch { return 'cash'; } });
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  // All students with outstanding packages
  const outstanding = finance
    .map(f => {
      const pkgs = (f.packages || []).filter(p => Math.max(0, p.total_price - (p.paid_amount || 0)) > 0);
      const total = pkgs.reduce((s, p) => s + Math.max(0, p.total_price - (p.paid_amount || 0)), 0);
      return { student: f.student, pkgs, total };
    })
    .filter(x => x.total > 0);

  const grandTotal = outstanding.reduce((s, x) => s + x.total, 0);

  const handleConfirm = async () => {
    if (!confirmed) { setConfirmed(true); return; }
    setSaving(true);
    try {
      localStorage.setItem('bachata_last_method', method);
      const paymentDate = date ? date + 'T12:00' : format(new Date(), "yyyy-MM-dd'T'HH:mm");
      const calls = outstanding.flatMap(({ student, pkgs }) =>
        pkgs.map(pkg =>
          api.post('/payments', {
            student_id: student.id,
            amount: parseFloat(Math.max(0, pkg.total_price - (pkg.paid_amount || 0)).toFixed(2)),
            date: paymentDate,
            method,
            notes: 'Solde groupé',
            package_id: pkg.id,
            group_course_id: null,
          })
        )
      );
      await Promise.all(calls);
      toast.success(`✓ ${outstanding.length} élèves soldés · ${grandTotal.toFixed(0)} CHF`);
      onPaid();
    } catch { toast.error('Erreur lors du paiement groupé'); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <div className="font-extrabold text-slate-900 font-manrope">Tout solder</div>
            <div className="text-sm text-slate-500">{outstanding.length} élève{outstanding.length !== 1 ? 's' : ''} en attente</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
            <X size={15} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Total */}
          <div className="bg-slate-50 rounded-2xl p-4 text-center">
            <div className="text-4xl font-extrabold text-slate-900 font-manrope">
              {grandTotal.toFixed(0)} <span className="text-xl text-slate-400">CHF</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">{outstanding.length} élève{outstanding.length !== 1 ? 's' : ''}</div>
          </div>

          {/* Liste des élèves concernés */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Élèves concernés</div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
              {outstanding.map(({ student, total }) => (
                <div key={student.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-xl">
                  <span className="text-sm font-semibold text-slate-700">{student.first_name} {student.last_name}</span>
                  <span className="text-sm font-bold text-slate-900">{total.toFixed(0)} CHF</span>
                </div>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Date de paiement</label>
            <input type="date" value={date}
              onChange={e => { setDate(e.target.value); setConfirmed(false); }}
              className="input-base" data-testid="solde-all-date" />
          </div>

          {/* Method */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Méthode</label>
            <div className="flex gap-2">
              {[['cash', 'Cash'], ['twint', 'Twint'], ['virement', 'Virement']].map(([val, label]) => (
                <button key={val} onClick={() => { setMethod(val); setConfirmed(false); }}
                  className={cn('flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors',
                    method === val ? 'bg-[#26A69A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                  data-testid={`solde-all-method-${val}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 space-y-2 shrink-0">
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 transition-colors">
              Annuler
            </button>
            <button onClick={handleConfirm} disabled={saving || grandTotal <= 0}
              className={cn('flex-1 h-12 rounded-xl font-bold text-base transition-all',
                confirmed ? 'bg-red-500 text-white' : 'bg-slate-700 text-white hover:bg-slate-800')}
              data-testid="confirm-solde-all-btn">
              {saving ? '…' : confirmed ? `CONFIRMER ${grandTotal.toFixed(0)} CHF` : `Tout solder · ${grandTotal.toFixed(0)} CHF`}
            </button>
          </div>
          {confirmed && !saving && (
            <p className="text-center text-xs text-red-400">
              Cliquez à nouveau pour confirmer (va dans la Corbeille)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
